// Radha — Dunning: escalating reminder ladder over the overdue book (Zoho
// Invoice/Billing style). Each overdue account is placed at the furthest ladder
// step it has reached; sending a step's reminder goes through the existing
// RBI-guarded collection_nudges path (window / daily cap / consent enforced there,
// so the coded error surfaces if a send is blocked).
import React, { useMemo, useState } from "react";

import { useAccounts } from "../hooks/useCollectionsDirect";
import { useSendNudge, writeErrorMessage } from "../hooks/useCollectionsWrites";
import { useDunningSteps, DEFAULT_LADDER, stepForDpd, dunningBody, type DunningStep } from "../hooks/useDunning";
import { usePromises, useLogPromise, promiseStatus, predictionErrorMessage } from "../hooks/usePredictions";
import { LegalEscalation } from "../components/LegalEscalation";
import { LimitationWatch } from "../components/GlobalDeskPanels";
import { useViewCountry } from "../lib/jurisdiction";
import { useMe, useRole } from "../lib/authSupabase";
import type { CollectionAccount } from "../types/collections";
import { fmtMoney as inr, fmtMoneyFull } from "../lib/money";


const TONE_COLOR: Record<string, string> = {
  friendly: "var(--status-good)", firm: "var(--accent-purple)", final: "var(--status-warning)", escalation: "var(--status-critical)",
};
const CHANNELS = new Set(["email", "sms", "whatsapp", "ivr_call"]);

const PROMISE_COLOR: Record<string, string> = {
  kept: "var(--status-good)", broken: "var(--status-critical)", pending: "var(--status-warning)",
};

function PromiseCell({ account, canWrite }: { account: CollectionAccount; canWrite: boolean }) {
  const { data: promises } = usePromises();
  const log = useLogPromise();
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState("");
  const [amount, setAmount] = useState(String(account.outstanding_amount));
  const [note, setNote] = useState<string | null>(null);

  const mine = (promises ?? []).filter((p) => p.account_id === account.id).sort((a, b) => b.created_at.localeCompare(a.created_at));
  const latest = mine[0];
  const status = latest ? promiseStatus(latest, Number(account.outstanding_amount)) : null;

  if (editing) {
    const submit = () => {
      if (!date || !(Number(amount) > 0)) return;
      setNote(null);
      log.mutate({ account_id: account.id, promised_date: date, promised_amount: Number(amount) }, {
        onSuccess: () => setEditing(false),
        onError: (e) => setNote(predictionErrorMessage(e)),
      });
    };
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
          style={{ padding: "2px 4px", fontSize: 11, border: "1px solid var(--border)", borderRadius: 4, background: "var(--surface)", color: "var(--fg)" }} />
        <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} style={{ width: 80, padding: "2px 4px", fontSize: 11, border: "1px solid var(--border)", borderRadius: 4, background: "var(--surface)", color: "var(--fg)" }} />
        <button onClick={submit} disabled={log.isPending} style={{ padding: "1px 6px", fontSize: 11, border: 0, borderRadius: 4, background: "var(--accent-purple)", color: "#fff", cursor: "pointer" }}>✓</button>
        <button onClick={() => setEditing(false)} style={{ padding: "1px 6px", fontSize: 11, border: "1px solid var(--border)", borderRadius: 4, background: "none", color: "var(--fg-muted)", cursor: "pointer" }}>×</button>
        {note && <span style={{ fontSize: 10.5, color: "var(--status-critical)" }}>{note}</span>}
      </span>
    );
  }

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      {latest ? (
        <span style={{ fontSize: 11.5, color: PROMISE_COLOR[status!] }} title={`Promised ${fmtMoneyFull(latest.promised_amount)} by ${latest.promised_date}`}>
          {status}
        </span>
      ) : <span style={{ fontSize: 11.5, color: "var(--fg-faint)" }}>none</span>}
      {canWrite && (
        <button onClick={() => setEditing(true)} style={{ padding: "1px 6px", fontSize: 10.5, border: "1px solid var(--border)", borderRadius: 4, background: "none", color: "var(--fg-muted)", cursor: "pointer" }}>
          log
        </button>
      )}
    </span>
  );
}

function AccountRow({ a, step, canWrite }: { a: CollectionAccount; step: DunningStep; canWrite: boolean }) {
  const send = useSendNudge();
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const channel = (CHANNELS.has(step.channel) ? step.channel : "email") as "email" | "sms" | "whatsapp" | "ivr_call";

  const doSend = () => {
    setNote(null);
    send.mutate(
      { account_id: a.id, channel, language: "en", body: dunningBody(step.tone, a.debtor_name, Number(a.outstanding_amount), a.days_past_due) },
      { onSuccess: () => setNote({ ok: true, text: `Sent via ${channel}` }), onError: (e) => setNote({ ok: false, text: writeErrorMessage(e) }) },
    );
  };

  return (
    <tr style={{ borderTop: "1px solid var(--border)" }}>
      <td style={{ padding: "8px" }}>{a.debtor_name}</td>
      <td style={{ padding: "8px", textAlign: "right", whiteSpace: "nowrap" }}>{inr(a.outstanding_amount)}</td>
      <td style={{ padding: "8px", textAlign: "right", color: a.days_past_due > 60 ? "var(--status-critical)" : "var(--fg)" }}>{a.days_past_due}d</td>
      <td style={{ padding: "8px" }}>
        {!a.consent_to_contact ? (
          <span style={{ fontSize: 12, color: "var(--fg-muted)" }} title="No contact consent — RBI blocks the send.">no consent</span>
        ) : canWrite ? (
          <button onClick={doSend} disabled={send.isPending}
            style={{ padding: "4px 10px", fontSize: 12, border: `1px solid ${TONE_COLOR[step.tone] ?? "var(--accent-purple)"}`, color: TONE_COLOR[step.tone] ?? "var(--accent-purple)", background: "none", borderRadius: 6, cursor: "pointer" }}>
            {send.isPending ? "Sending…" : `Send ${channel}`}
          </button>
        ) : <span style={{ fontSize: 12, color: "var(--fg-muted)" }}>—</span>}
        {note && <span style={{ marginLeft: 8, fontSize: 12, color: note.ok ? "var(--status-good)" : "var(--status-critical)" }}>{note.text}</span>}
      </td>
      <td style={{ padding: "8px" }}><PromiseCell account={a} canWrite={canWrite} /></td>
    </tr>
  );
}

export function DunningPanel() {
  const { data: accounts, isLoading, error } = useAccounts();
  const { data: ladderRows } = useDunningSteps();
  const { canWrite } = useRole();
  const me = useMe(true);
  const viewCountry = useViewCountry(me?.tenant_country);
  const ladder = ladderRows && ladderRows.length ? ladderRows : DEFAULT_LADDER;

  const grouped = useMemo(() => {
    const list = (accounts ?? []) as CollectionAccount[];
    const bySeq = new Map<number, CollectionAccount[]>();
    for (const a of list) {
      if (a.days_past_due <= 0) continue;
      const step = stepForDpd(ladder, a.days_past_due);
      if (!step) continue;
      (bySeq.get(step.seq) ?? bySeq.set(step.seq, []).get(step.seq)!).push(a);
    }
    return ladder.map((s) => {
      const rows = (bySeq.get(s.seq) ?? []).sort((a, b) => Number(b.outstanding_amount) - Number(a.outstanding_amount));
      return { step: s, rows, outstanding: rows.reduce((t, a) => t + Number(a.outstanding_amount), 0) };
    });
  }, [accounts, ladder]);

  if (isLoading) return <p style={{ padding: 24 }}>Loading…</p>;
  if (error) return <p style={{ padding: 24, color: "var(--status-critical)" }}>{(error as Error).message}</p>;

  const totalDue = grouped.reduce((t, g) => t + g.rows.length, 0);

  return (
    <div>
      <p style={{ color: "var(--fg-muted)", fontSize: 13, margin: "4px 0 16px" }}>
        {totalDue} overdue account{totalDue === 1 ? "" : "s"} across {ladder.length} escalation steps. Sends run through the
        contact-window / frequency-cap / consent guard for {me?.tenant_country ?? "your"} — blocked sends return the coded
        reason. The rule in force is shown under Settings &rsaquo; Jurisdiction.
      </p>

      {grouped.map(({ step, rows, outstanding }) => (
        <div key={step.seq} className="sanctuary-card" style={{ marginBottom: 22, overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "var(--surface-2)" }}>
            <span style={{ width: 22, height: 22, borderRadius: 11, background: TONE_COLOR[step.tone] ?? "var(--accent-purple)", color: "#fff", fontSize: 12, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{step.seq}</span>
            <span style={{ fontWeight: 600 }}>{step.label}</span>
            <span style={{ fontSize: 12, color: "var(--fg-muted)" }}>DPD ≥ {step.min_dpd} · {step.channel} · {step.tone}</span>
            <span style={{ marginLeft: "auto", fontSize: 13, color: "var(--fg-muted)" }}>{rows.length} · {inr(outstanding)}</span>
          </div>
          {rows.length > 0 && (
            <div className="sanctuary-scroll-x">
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 12 }}>
                  <th style={{ padding: "6px 8px", fontWeight: 500 }}>Debtor</th>
                  <th style={{ padding: "6px 8px", fontWeight: 500, textAlign: "right" }}>Outstanding</th>
                  <th style={{ padding: "6px 8px", fontWeight: 500, textAlign: "right" }}>DPD</th>
                  <th style={{ padding: "6px 8px", fontWeight: 500 }}>Action</th>
                  <th style={{ padding: "6px 8px", fontWeight: 500 }}>Promise</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => <AccountRow key={a.id} a={a} step={step} canWrite={canWrite} />)}
              </tbody>
            </table>
            </div>
          )}
        </div>
      ))}

      <LegalEscalation accounts={(accounts ?? []) as CollectionAccount[]} tenantName={me?.tenant_legal_name ?? "Radha"} tenantGstin={me?.tenant_gstin ?? null} tenantIsMsme={me?.tenant_is_msme ?? false} tenantCountry={viewCountry} canWrite={canWrite} />
      <LimitationWatch />
    </div>
  );
}
